/* eslint-disable max-len */
import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, pitchType, pitchSubType, pitchTypeColor, pitchSubTypeColor, pitchTypeDisplayName } from 'yankees-react-utils';
import ReportCard, { reportCardBorder } from '../../../../components/ReportCard';
import { mapPPFXReport } from '../../../../data/dataMapping';
import StateTabs from '../../../../components/StateTabs';
import TopMenu from '../Overview/TopMenu';
import { useOktaQueries, PitcherStrengths, Stats, StrikeZones, PlayerReport, HeatMaps } from '../../../../data';
import usePlayerSelector from '../../../../utils/usePlayerSelector';
import ReportNote from '../../../../components/ReportNote';
import PitchTypeCharts from '../PPFX/PitchTypeCharts';
import PitchLocationsCharts from '../PPFX/PitchLocationCharts';
import PitchBreakPlotChart from '../../../../components/PitchBreakPlotChart';

const StyledNotesContainer = styled.div`
    text-align: left;
    padding: 10 px;
    margin: 5px;
    h4 {
        font-size:18px;
        font-weight: bold;
    }
    @media (max-width: 1112px) {
        > div > div > div > p > img {
            height: 100% !important;
            width: 100% !important;
        }
    }
    
`;
const StyledPPFXPitchTabs = styled.div`
    > div + div {
        margin: 0;
        border: ${reportCardBorder};
        border-radius: 0.25rem;
    }
`;

const buildQueries = (pitcher, year, level, isLhb, role) => {
    if (pitcher === null) return [];
    const islhp = pitcher?.Throws === 2;
    const yearUseChartingDiff = year?.value.endYear - year?.value.startYear;
    const yearUseCharting = yearUseChartingDiff > 0 ? yearUseChartingDiff * -1 : year?.value.endYear;
    // drop time part from date parameters so these calls work for web and Dugout builds
    const queries = [
        { queryKey: ['pitchbreakdown', `${year?.value.startYear}-${year?.value.endYear}`,
            pitcher?.PlayerID,
            level,
            isLhb,
            role],
        queryFn: (t) => PitcherStrengths.getPitchBreakdown(t,
            { pitcherId: pitcher?.PlayerID,
                startYear: year?.value.startYear,
                endYear: year?.value.endYear,
                level: level?.value,
                islhb: isLhb?.value,
                role: role?.value }),
        enabled: !!pitcher?.PlayerID && !!year?.value.startYear && !!year?.value.endYear && !!level && !!isLhb && !!role },
        { queryKey: ['lgaStats', islhp], queryFn: (t) => Stats.pitchtypebreakdownpitchfxaverages(t, { islhp, islhb: '' }) },
        { queryKey: ['strikeZoneContoursLhb', yearUseCharting], queryFn: (t) => StrikeZones.getStrikeZoneContours(t, { islhb: true, yearid: yearUseCharting, levelid: level?.value }), enabled: !!yearUseCharting },
        { queryKey: ['strikeZoneContoursRhb', yearUseCharting], queryFn: (t) => StrikeZones.getStrikeZoneContours(t, { islhb: false, yearid: yearUseCharting, levelid: level?.value }), enabled: !!yearUseCharting },
        { queryKey: ['ppfxAvgs', year?.value.startYear, year?.value.endYear, islhp], queryFn: (t) => PlayerReport.ppfxavgs(t, { minyear: year?.value.startYear, maxyear: year?.value.endYear, islhp }), enabled: !!year?.value.startYear && !!year?.value.endYear },
        { queryKey: ['heatMapsLocations', pitcher?.PlayerID, yearUseCharting], queryFn: (t) => HeatMaps.pitchLocationActual(t, pitcher?.PlayerID, { yearid: yearUseCharting }), enabled: !!yearUseCharting, retry: false },
        { queryKey: ['heatMapsPts', pitcher?.PlayerID], queryFn: (t) => HeatMaps.getPitcherHeatMaps(t, pitcher?.PlayerID), retry: false },
    ];
    return queries;
};

const mapLeagueAverageSeries = (translate, lgastats) => lgastats?.map((pt) => ({
    type: 'scatter',
    data: [{
        x: Math.round(pt.breakx54 * 10) / 10,
        y: Math.round(pt.breakz54 * 10) / 10,
        color: pitchTypeColor(pt.pitchtype),
    }],
    linkedTo: pt.pitchtype.toString(), // add support for subtypes once they're in pitch averages
    name: `MLB ${translate('common.abbr-average')} ${pitchTypeDisplayName(pt)}`,
    marker: { radius: 6, color: pitchTypeColor(pt.pitchtype), lineColor: 'white', lineWidth: 1 },
    tooltip: {
        pointFormat: `${translate('baseball.break-x')}: {point.x} ${translate('components.search.abbr-inches')}<br />${translate('baseball.break-z')}: {point.y} ${translate('components.search.abbr-inches')}`,
    },

}));

const mapDataQueriesResponse = (translate, dataQueries, player, playerId, startYear) => {
    const ppfxParams = { playerId };
    let ppfx = dataQueries[0]?.data || {};
    if (ppfx && ppfx.pitches !== undefined) {
        ppfx = mapPPFXReport(translate, ppfxParams, ppfx, true, startYear);
    }
    const lgastats = dataQueries[1]?.data || [];
    let lgaSeries = [];
    if (ppfx && ppfx.pitchtypes !== undefined) {
        const pitchTypes = ppfx?.pitchtypes?.map((x) => x.pitchtype);
        lgaSeries = mapLeagueAverageSeries(translate, lgastats.filter((x) => pitchTypes.indexOf(x.pitchtype) >= 0 && x.pitchtype !== 1));
    }
    const strikeZoneContoursLhb = dataQueries[2]?.data || {};
    const strikeZoneContoursRhb = dataQueries[3]?.data || {};
    const strikeZoneContours = {
        lhb: [],
        rhb: [],
    };
    const lhbSZContours = strikeZoneContoursLhb;
    const rhbSZContours = strikeZoneContoursRhb;
    strikeZoneContours.lhb = player.Throws === 1 ? lhbSZContours.rhp : lhbSZContours.lhp;
    strikeZoneContours.rhb = player.Throws === 1 ? rhbSZContours.rhp : rhbSZContours.lhp;
    const ppfxAvgs = dataQueries[4]?.data || {};
    const heatMapsLocations = dataQueries[5]?.data || [];
    const heatMapsPts = dataQueries[6]?.data || [];
    return {
        lgastats,
        ppfx,
        strikeZoneContours,
        ppfxAvgs,
        heatMapsLocations,
        heatMapsPts,
        lgaSeries,
        pitches: dataQueries[0]?.data || {},
    };
};

const PitchBreakdown = () => {
    const { t: translate } = useTranslation();
    const player = usePlayerSelector();

    const [pitcher, setPitcher] = useState(player.IsCatcher ? null : player);
    const [isLoadingFilters, setIsLoadingFilters] = useState(true);
    const [year, setYear] = useState(null);
    const [level, setLevel] = useState(null);
    const [isLhb, setIsLhb] = useState(null);
    const [role, setRole] = useState(null);
    const [hasSearched, setHasSearched] = useState(!player.IsCatcher);
    const [dataQueries, setDataQueries] = useState([]);

    const getData = () => {
        if (player) {
            setDataQueries(buildQueries(pitcher, year, level, isLhb, role));
        }
    };
    const dataQueriesResponse = useOktaQueries(dataQueries);
    const isDataLoading = dataQueriesResponse.some((q) => q.isLoading || q.isFetching) || !dataQueries.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const data = useMemo(() => (isDataLoading ? {} : mapDataQueriesResponse(translate, dataQueriesResponse, pitcher, pitcher?.PlayerID, year?.value.startYear)), [year, pitcher, level, isLhb, role, isDataLoading]);
    useEffect(() => {
        getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, pitcher, level, isLhb, role]);

    const getSeasonStats = (type) => data.ppfx.seasonstats.filter(({ pitchtype, fxsubtype }) => type.pitchtype === pitchtype && type.fxsubtype === fxsubtype);
    const games = [...new Set(data.ppfx?.pitches?.map((p) => p.gameid))].length;
    const pitchStatTabs = data.ppfx?.pitchtypes?.filter((type) => type.pitchtype !== 1)?.map((type) => {
        const ppfxPitches = { ...data.pitches, pitches: data.pitches?.pitches.filter(({ pitchtype, fxsubtype }) => type.pitchtype === pitchtype && type.fxsubtype === fxsubtype) };
        const ppfx = mapPPFXReport(translate, { playerId: pitcher?.PlayerID }, ppfxPitches, true, year?.value.startYear);
        const key = (1000 * type.pitchtype) + type.fxsubtype || 0;
        return {
            key,
            title: `${pitchType(type.pitchtype, undefined, undefined, translate)}${type.fxsubtype > 0 ? ` (${pitchSubType(type.fxsubtype, undefined, undefined, translate)})` : ''}`,
            component: (
                <>
                    <h4>{`${pitchType(type.pitchtype || 0, undefined, undefined, translate)} ${translate('sections.reports.pitcher.stats')}`}</h4>
                    <StyledNotesContainer>
                        <h4>{translate('sections.reports.pitcher.notes')}</h4>
                        <ReportNote type="pts" section={13} recordId={type.pitchtype} playerId={pitcher?.PlayerID} />
                        <ReportNote type="pts" section={23} recordId={type.pitchtype} playerId={pitcher?.PlayerID} />
                    </StyledNotesContainer>
                    <PitchTypeCharts
                        playerid={pitcher?.PlayerID}
                        type={type}
                        pitchtypeseries={ppfx.pitchtypeseries}
                        gamestats={[]}
                        seasonstats={getSeasonStats(type)}
                        lgastats={data.lgastats.filter(({ pitchtype, fxsubtype }) => type.pitchtype === pitchtype && type.fxsubtype === fxsubtype)}
                        islhp={pitcher?.Throws === 2}
                        strikeZoneContours={data.strikeZoneContours}
                        pitchtypestdev={data.ppfxAvgs.pitchtypestdev}
                        dataPoints={games}
                        dataPointType="Game"
                        heatMapsLocations={data.heatMapsLocations != null ? data.heatMapsLocations.filter((h) => h.PitchType === type.pitchtype && ((h.FxSubType === null && (type.fxsubtype || 0) === 0) || h.FxSubType === type.fxsubtype)) : []}
                        heatMapsPts={data.heatMapsPts != null ? data.heatMapsPts.filter((h) => h.PitchType === type.pitchtype && ((h.FxSubType === null && (type.fxsubtype || 0) === 0) || h.FxSubType === type.fxsubtype)) : []}
                        useSubType
                        showGameFxEra={false}
                    />
                </>
            ),
        };
    }).filter((x) => x);
    return (
        <div>
            <div className={isLoadingFilters ? '' : 'd-flex justify-content-between align-items-center'}>
                <h3 className="mb-0">{translate('sections.reports.pitcher.pitchbreakdown-title')}</h3>
                <TopMenu
                    setIsLoadingFilters={setIsLoadingFilters}
                    pitcher={pitcher}
                    setPitcher={setPitcher}
                    year={year}
                    setYear={setYear}
                    level={level}
                    setLevel={setLevel}
                    isLhb={isLhb}
                    setIsLhb={setIsLhb}
                    role={role}
                    setRole={setRole}
                    setHasSearched={setHasSearched}
                />
            </div>
            {!hasSearched && <div className="text-center py-4 px-3 mt-3">{translate('sections.reports.pitcher.use-filters')}</div>}
            { !isLoadingFilters && hasSearched && (
                isDataLoading ? (
                    <div className="d-flex justify-content-center py-5">
                        <LoadingSpinner />
                    </div>
                ) : (
                    <>
                        {pitchStatTabs && !!pitchStatTabs.length ? (
                            <>
                                <StyledPPFXPitchTabs>
                                    <StateTabs tabs={pitchStatTabs} />
                                </StyledPPFXPitchTabs>
                                <ReportCard title={translate('sections.reports.pitcher.pitch-break-plot')}>
                                    <PitchBreakPlotChart
                                        enableZoom={false}
                                        pitches={data.ppfx.pitches}
                                        seriesData={data.lgaSeries}
                                        width={250}
                                        height={350}
                                        showlegend
                                        keyX="breakx54"
                                        keyY="breakz54"
                                        colorFunc={pitchSubTypeColor}
                                    />
                                </ReportCard>
                                <ReportCard title={translate('sections.reports.pitcher.pitch-location-count')}>
                                    <PitchLocationsCharts countseries={data.ppfx.countseries} strikeZoneContours={data.strikeZoneContours} />
                                </ReportCard>
                            </>
                        ) : <div className="text-center bg-white border rounded py-4 px-3 mt-3">{translate('pitcher-overview.no-data')}</div>}
                    </>
                ))}
        </div>
    );
};

export default PitchBreakdown;
